import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  CardMedia,
  Checkbox,
  FormControlLabel,
  Input,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from "@mui/material";
import { ChangeEvent, useState } from "react";
import SwitchToggle from "../../components/SwitchToggle";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CloseIcon from "@mui/icons-material/Close";

type FeatureSubGroup = {
  name: string;
  features: string[];
};

type FeatureGroup = {
  name: string;
  type: "checkbox" | "select" | "mixed" | "group";
  features?: string[];
  options?: string[];
  selectOptions?: string[];
  subGroups?: FeatureSubGroup[];
};

const AddEditVehicle = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAddUser = location.pathname === "/manage-vehicles/add";

  const [showroomImage, setShowroomImage] = useState<string>("");

  const dealers = ["Tesla", "Honda", "Toyota", "Ford"];
  const categories = [
    "Car",
    "Bike",
    "Bicycle",
    "Caravan",
    "Trucks",
    "Heavy Equipment"
  ];

  const subCategories = [
    "Engine",
    "Suspension",
    "Transmission",
    "Electrical",
    "Body Parts",
    "Brakes",
    "Tires & Wheels",
    "Oils & Fluids",
    "Accessories",
  ];

  const conditions = ["New", "Used", "Refurbished"];
  const brands = ["Tesla", "Honda", "Toyota", "Ford"];
  const models = ["Model X", "Model Y", "Civic", "Corolla"];

  const featureGroups: FeatureGroup[] = [
    {
      name: "Security",
      type: "checkbox",
      features: [
        "Anti-lock braking system (ABS)",
        "Electronic Stability Program (ESP)",
      ],
    },
    {
      name: "Cruise control",
      type: "select",
      options: ["Standard", "Adaptive", "Intelligent"],
    },
    {
      name: "Air conditioning",
      type: "mixed",
      selectOptions: ["Manual", "Automatic"],
      features: [
        "Stationary air conditioning",
        "Auxiliary heater",
        "Heated seats",
      ],
    },
    {
      name: "Parking assistance",
      type: "group",
      features: ["Self-steering systems"],
      subGroups: [
        {
          name: "Acoustic parking aid",
          features: ["In front", "Rear"],
        },
        {
          name: "Visual parking aid",
          features: ["Camera", "360° camera"],
        },
      ],
    },
    {
      name: "Interior",
      type: "group",
      subGroups: [
        {
          name: "Seats",
          features: [
            "Central seating group",
            "Round seating area",
            "Side seating group",
          ],
        },
        {
          name: "Sleeping accommodations",
          features: ["Fixed bed", "Bunk bed"],
        },
        {
          name: "Sanitary facilities",
          features: ["WC", "Separate shower"],
        },
      ],
    },
    {
      name: "Additional comfort features",
      type: "checkbox",
      features: ["Central locking", "Power steering"],
    },
    {
      name: "Infotainment",
      type: "checkbox",
      features: ["TV", "Navigation system"],
    },
    {
      name: "Extras",
      type: "checkbox",
      features: ["Rear garage", "Awning", "Solar system"],
    },
  ];

  const [selectedInteriorColor, setSelectedInteriorColor] = useState("");
  const [selectedExteriorColor, setSelectedExteriorColor] = useState("");
  const allColors = [
    "Beige",
    "Blue",
    "Brown",
    "Bronze",
    "Yellow",
    "Grey",
    "Green",
    "Red",
    "Black",
    "Silver",
    "Violet",
    "White",
    "Orange",
    "Gold",
  ];
  const colorMap: Record<string, string> = {
    Beige: "#F5F5DC",
    Blue: "#0000FF",
    Brown: "#8B4513",
    Bronze: "#CD7F32",
    Yellow: "#FFFF00",
    Grey: "#808080",
    Green: "#008000",
    Red: "#FF0000",
    Black: "#000000",
    Silver: "#C0C0C0",
    Violet: "#EE82EE",
    White: "#FFFFFF",
    Orange: "#FFA500",
    Gold: "#FFD700",
  };

  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null);

  return (
    <>
      {/* PAGE TITLE */}
      <div className="main_title">
        <h1>{isAddUser ? "Add" : "Edit"} Vehicle</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-vehicles")}>Listed Vehicles</button>{" "}
          - {isAddUser ? "Add" : "Edit"} Vehicle
        </p>
      </div>

      <div className="form">

        <div className="cards">
          <div className="table_header mb_20">
            <h2>Upload Vehicle Images & Video</h2>
          </div>

          <div className="gap_p">
            {/* Image Upload */}
            <div className="control_group w_100">
              <label>Upload Images <small>- 0 of 15 Images (JPG, max. 10 MB per Image)</small></label>
              <div className="gap_p">
                {[1, 2, 3].map((_, i) => (
                  <div className="w_25" key={i}>
                    {showroomImage ? (
                      <div className="upload_image no_radius v2">
                        <div className="upload_image_holder">
                          <figure>
                            <CardMedia component="img" image={showroomImage} />
                          </figure>
                          <CloseIcon
                            className="remove_img"
                            onClick={() => setShowroomImage("")}
                          />
                        </div>
                      </div>
                    ) : (
                      <label className="upload_image no_radius v2">
                        <Input
                          sx={{ display: "none" }}
                          type="file"
                          inputProps={{ accept: "image/*" }}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            const files = event.target.files;
                            if (files && files[0]) {
                              setShowroomImage(URL.createObjectURL(files[0]));
                            }
                          }}
                        />
                        <span className="upload_image_holder">
                          <figure className="add_img">
                            <img
                              src="/static/images/placeholder.png"
                              alt=""
                            />
                            <span className="upload_img_icon">
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt=""
                              />
                            </span>
                          </figure>
                        </span>
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Video Upload */}
            <div className="control_group w_100">
              <label>YouTube video</label>
              <TextField fullWidth hiddenLabel placeholder="https://www.youtube.com/watch?v=zyx"></TextField>
            </div>
          </div>
        </div>

        <div className="cards">
          <div className="table_header mb_20">
            <h2>Basic Information</h2>
          </div>

          <div className="gap_p">

            <div className="control_group w_100">
              <label>Ad Title</label>
              <TextField placeholder="Enter Ad Title" hiddenLabel fullWidth />
            </div>

            <div className="control_group w_33">
              <label>Category</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select Category</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </div>

            <div className="control_group w_33">
              <label>Sub Category</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select Sub Category</MenuItem>
                {subCategories.map((sub) => (
                  <MenuItem key={sub} value={sub}>{sub}</MenuItem>
                ))}
              </Select>
            </div>

            <div className="control_group w_33">
              <label>Dealer</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select Dealer</MenuItem>
                {dealers.map((dealer) => (
                  <MenuItem key={dealer} value={dealer}>{dealer}</MenuItem>
                ))}
              </Select>
            </div>

            <div className="control_group w_33">
              <label>Price</label>
              <TextField
                id="price"
                fullWidth
                hiddenLabel
                placeholder="Enter Price"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="adornment_select">
                      <Select fullWidth hiddenLabel defaultValue="default">
                        <MenuItem value="default">Fixed</MenuItem>
                        <MenuItem value="2">Negotiable</MenuItem>
                      </Select>
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <div className="control_group w_33">
              <label>Location</label>
              <TextField placeholder="Enter Location" hiddenLabel fullWidth />
            </div>

            <div className="control_group w_33">
              <label>Condition</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select Condition</MenuItem>
                {conditions.map((cond) => (
                  <MenuItem key={cond} value={cond}>{cond}</MenuItem>
                ))}
              </Select>
            </div>

            <div className="control_group w_100">
              <label>Small Description</label>
              <TextField multiline rows={2} placeholder="Enter short description" hiddenLabel fullWidth />
            </div>

            <div className="control_group w_100">
              <label>Full Description</label>
              <TextField multiline rows={5} placeholder="Enter full description" hiddenLabel fullWidth />
            </div>

            <div className="control_group w_33">
              <label>Vehicle Number</label>
              <TextField
                placeholder="e.g. D9_647949"
                hiddenLabel
                fullWidth
              />
            </div>

            <div className="control_group w_33">
              <label>Availability</label>
              <TextField
                placeholder="e.g. Immediately"
                hiddenLabel
                fullWidth
              />
            </div>

            <div className="control_group flex w_100">
              <label>Is this Featured Vehicle?</label>
              <SwitchToggle />
            </div>

            <div className="control_group flex w_100">
              <label>Leasing Black Week Deal?</label>
              <SwitchToggle />
            </div>

          </div>
        </div>

        <div className="cards">
          <div className="table_header mb_20">
            <h2>Technical Data</h2>
          </div>

          <div className="gap_p">

            <div className="control_group w_33">
              <label>Brand</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select Brand</MenuItem>
                {brands.map((brand) => (
                  <MenuItem key={brand} value={brand}>{brand}</MenuItem>
                ))}
              </Select>
            </div>

            <div className="control_group w_33">
              <label>Model</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select Model</MenuItem>
                {models.map((model) => (
                  <MenuItem key={model} value={model}>{model}</MenuItem>
                ))}
              </Select>
            </div>

            <div className="control_group w_33">
              <label>First registration</label>
              <div className="gap_p">
                <div className="control_group w_50">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      views={["year"]}
                      value={year}
                      onChange={(newValue) => setYear(newValue)}
                      className="form_control dateTime_picker"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          id: "registration-year",
                          placeholder: "Select Year",
                          hiddenLabel: true,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </div>

                <div className="control_group w_50">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      views={["month"]}
                      value={month}
                      onChange={(newValue) => setMonth(newValue)}
                      className="form_control dateTime_picker"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          id: "registration-month",
                          placeholder: "Select Month",
                          hiddenLabel: true,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </div>
              </div>
            </div>

            <div className="control_group w_33">
              <label>Year of construction</label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  views={["year"]}
                  value={year}
                  onChange={(newValue) => setYear(newValue)}
                  className="form_control dateTime_picker"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      id: "construction-year",
                      placeholder: "Select Year",
                      hiddenLabel: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </div>

            <div className="control_group w_33">
              <label>Number of beds</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select</MenuItem>
                <MenuItem value="1">2</MenuItem>
                <MenuItem value="2">4</MenuItem>
                <MenuItem value="3">6</MenuItem>
                <MenuItem value="4">8</MenuItem>
              </Select>
            </div>

            <div className="control_group w_33">
              <label>Length</label>
              <TextField
                id="price"
                fullWidth
                hiddenLabel
                placeholder="Enter Length"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="size_sm">mm</InputAdornment>
                  ),
                }}
              />
            </div>

            <div className="control_group w_33">
              <label>Width</label>
              <TextField
                id="price"
                fullWidth
                hiddenLabel
                placeholder="Enter Width"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="size_sm">mm</InputAdornment>
                  ),
                }}
              />
            </div>

            <div className="control_group w_33">
              <label>Height</label>
              <TextField
                id="price"
                fullWidth
                hiddenLabel
                placeholder="Enter Height"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="size_sm">mm</InputAdornment>
                  ),
                }}
              />
            </div>

            <div className="control_group w_33">
              <label>Maximum Weight Allowed</label>
              <TextField
                id="price"
                fullWidth
                hiddenLabel
                placeholder="Enter Maximum Weight Allowed"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="size_sm">kg</InputAdornment>
                  ),
                }}
              />
            </div>

            <div className="control_group w_33">
              <label>Axles</label>
              <TextField
                id="price"
                fullWidth
                hiddenLabel
                placeholder="Enter Axles"
              />
            </div>

            <div className="control_group w_33">
              <label>Fuel Type</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select Fuel</MenuItem>
                <MenuItem value="Petrol">Petrol</MenuItem>
                <MenuItem value="Diesel">Diesel</MenuItem>
                <MenuItem value="Electric">Electric</MenuItem>
              </Select>
            </div>

            <div className="control_group w_33">
              <label>Engine Power</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select</MenuItem>
                <MenuItem value="1">150 HP</MenuItem>
                <MenuItem value="2">200 HP</MenuItem>
                <MenuItem value="3">250 HP</MenuItem>
                <MenuItem value="4">300 HP</MenuItem>
              </Select>
            </div>

            <div className="control_group w_33">
              <label>Transmission</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select</MenuItem>
                <MenuItem value="1">Manual</MenuItem>
                <MenuItem value="2">Automatic</MenuItem>
                <MenuItem value="3">CVT</MenuItem>
                <MenuItem value="4">Semi-Automatic</MenuItem>
              </Select>
            </div>

            <div className="control_group w_33">
              <label>Drive Type</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select</MenuItem>
                <MenuItem value="1">FWD</MenuItem>
                <MenuItem value="2">RWD</MenuItem>
                <MenuItem value="3">AWD</MenuItem>
                <MenuItem value="4">4WD</MenuItem>
              </Select>
            </div>

            <div className="control_group w_33">
              <label>Emission class</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select</MenuItem>
                <MenuItem value="1">Euro 6</MenuItem>
                <MenuItem value="2">Euro 5</MenuItem>
                <MenuItem value="3">Euro 5</MenuItem>
                <MenuItem value="4">Euro 3</MenuItem>
              </Select>
            </div>

            <div className="control_group w_33">
              <label>Environmental badge</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select</MenuItem>
                <MenuItem value="1">Green Badge</MenuItem>
                <MenuItem value="2">Blue Badge</MenuItem>
                <MenuItem value="3">Yellow Badge</MenuItem>
                <MenuItem value="4">Red Badge</MenuItem>
              </Select>
            </div>

            <div className="control_group w_33">
              <label>Mileage</label>
              <TextField
                id="price"
                fullWidth
                hiddenLabel
                placeholder="Enter Mileage"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="size_sm">km</InputAdornment>
                  ),
                }}
              />
            </div>

            <div className="control_group w_33">
              <label>Vehicle Type</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select</MenuItem>
                <MenuItem value="1">Sedan</MenuItem>
                <MenuItem value="2">SUV</MenuItem>
                <MenuItem value="3">Truck</MenuItem>
                <MenuItem value="4">Hatchback</MenuItem>
              </Select>
            </div>

            <div className="control_group w_33">
              <label>Number of owners of the vehicle</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select</MenuItem>
                <MenuItem value="1">1</MenuItem>
                <MenuItem value="2">2</MenuItem>
                <MenuItem value="3">3</MenuItem>
                <MenuItem value="4">4</MenuItem>
              </Select>
            </div>

            <div className="control_group w_33">
              <label>Damaged car</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select</MenuItem>
                <MenuItem value="1">Yes</MenuItem>
                <MenuItem value="2">No</MenuItem>
              </Select>
            </div>

            <div className="control_group w_33">
              <label>HU valid until</label>
              <div className="gap_p">
                <div className="control_group w_50">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      views={["year"]}
                      value={year}
                      onChange={(newValue) => setYear(newValue)}
                      className="form_control dateTime_picker"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          id: "registration-year",
                          placeholder: "Select Year",
                          hiddenLabel: true,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </div>

                <div className="control_group w_50">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      views={["month"]}
                      value={month}
                      onChange={(newValue) => setMonth(newValue)}
                      className="form_control dateTime_picker"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          id: "registration-month",
                          placeholder: "Select Month",
                          hiddenLabel: true,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </div>
              </div>
            </div>

            <div className="control_group w_33">
              <label>GCC</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </div>

            <div className="control_group w_33">
              <label>Miles</label>
              <TextField placeholder="Enter Miles" hiddenLabel fullWidth />
            </div>

            <div className="control_group w_33">
              <label>Manufacture Color</label>
              <TextField placeholder="Enter Color" hiddenLabel fullWidth />
            </div>
          </div>
        </div>

        <div className="cards">
          <div className="table_header mb_20">
            <h2>Vehicle Color</h2>
          </div>

          <div className="cards bordered">
            <div className="table_header mb_20">
              <h3>Interior</h3>
            </div>

            <div className="gap_p">
              <div className="control_group w_100">
                <label style={{ marginBottom: "15px" }}>Color</label>

                <RadioGroup
                  value={selectedInteriorColor}
                  onChange={(e) => setSelectedInteriorColor(e.target.value)}
                  className="gap_p gap_sm"
                >
                  {allColors.map((color) => (
                    <div className="w_25" key={color}>
                      <FormControlLabel
                        value={color}
                        control={<Radio />}
                        label={
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span
                              style={{
                                width: "20px",
                                height: "20px",
                                borderRadius: "50%",
                                backgroundColor: colorMap[color],
                                border: "1px solid #ccc",
                              }}
                            />
                            <span>{color}</span>
                          </div>
                        }
                      />
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div className="control_group w_100">
                <label style={{ marginBottom: "10px" }}>Painting</label>
                <RadioGroup defaultValue="any" className="flex">
                  <FormControlLabel value="any" control={<Radio />} label="Any" />
                  <FormControlLabel value="metallic" control={<Radio />} label="Metallic" />
                </RadioGroup>
              </div>
            </div>
          </div>

          <div className="cards bordered">
            <div className="table_header mb_20">
              <h3>Exterior</h3>
            </div>

            <div className="gap_p">
              <div className="control_group w_100">
                <label style={{ marginBottom: "15px" }}>Color</label>

                <RadioGroup
                  value={selectedExteriorColor}
                  onChange={(e) => setSelectedExteriorColor(e.target.value)}
                  className="gap_p gap_sm"
                >
                  {allColors.map((color) => (
                    <div className="w_25" key={color}>
                      <FormControlLabel
                        value={color}
                        control={<Radio />}
                        label={
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span
                              style={{
                                width: "20px",
                                height: "20px",
                                borderRadius: "50%",
                                backgroundColor: colorMap[color],
                                border: "1px solid #ccc",
                              }}
                            />
                            <span>{color}</span>
                          </div>
                        }
                      />
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div className="control_group w_100">
                <label style={{ marginBottom: "10px" }}>Painting</label>
                <RadioGroup defaultValue="any" className="flex">
                  <FormControlLabel value="any" control={<Radio />} label="Any" />
                  <FormControlLabel value="metallic" control={<Radio />} label="Metallic" />
                </RadioGroup>
              </div>
            </div>
          </div>
        </div>

        <div className="cards">
          <div className="table_header mb_20">
            <h2>Features</h2>
          </div>

          {featureGroups.map((group) => (
            <div key={group.name} className="cards bordered">
              <div className="gap_p">
                <div className="control_group w_100">
                  <label style={{ marginBottom: "10px", fontWeight: '600' }}>{group.name}</label>

                  {/* SELECT TYPE */}
                  {group.type === "select" && group.options && (
                    <Select fullWidth hiddenLabel defaultValue="default">
                      <MenuItem value="default" disabled>Select</MenuItem>
                      {group.options.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  )}

                  {/* MIXED TYPE */}
                  {group.type === "mixed" && (
                    <>
                      <div className="gap_p">
                        <div className="control_group w_100">
                          <Select fullWidth hiddenLabel defaultValue="default">
                            <MenuItem value="default" disabled>Select</MenuItem>
                            {group.selectOptions?.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        </div>

                        <div className="control_group w_100">
                          <div className="gap_p gap_sm">
                            {group.features?.map((f) => (
                              <div className="w_33" key={f}>
                                <FormControlLabel control={<Checkbox />} label={f} />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* CHECKBOX TYPE */}
                  {group.type === "checkbox" && group.features && (
                    <div className="gap_p gap_sm">
                      {group.features.map((f) => (
                        <div className="w_33" key={f}>
                          <FormControlLabel control={<Checkbox />} label={f} />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* GROUP TYPE (with SubGroups) */}
                  {group.type === "group" && (
                    <>
                      <div className="gap_p">
                        {/* Group Features */}
                        {group.features && (
                          <div className="control_group w_100">
                            <div className="gap_p gap_sm mb_10">
                              {group.features.map((f) => (
                                <div className="w_33" key={f}>
                                  <FormControlLabel control={<Checkbox />} label={f} />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="control_group w_100">
                          <div className="gap_p">
                            {/* Sub Groups */}
                            {group.subGroups?.map((sub) => (
                              <div className="control_group w_100">
                                <label style={{ marginBottom: "10px", fontWeight: '500', fontSize: "13px" }}>{sub.name}</label>

                                <div className="gap_p gap_sm">
                                  {sub.features.map((f) => (
                                    <div className="w_33" key={f}>
                                      <FormControlLabel control={<Checkbox />} label={f} />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* SAVE BUTTON */}
        < div className="form_btn" >
          <Button className="btn btn_primary">Save</Button>
        </div>
      </div >
    </>
  );
};

export default AddEditVehicle;